"use client";
// ===================================================
// GAM Command Center — Pinkas (פנקס) queries
// Shared Gal<->Claude notebook (cc_pinkas). Both write: Gal via UI, Claude via SQL/MCP.
// Polled so each side sees the other's entries. Spec: docs/_live/memory/APP-pinkas.md.
// ===================================================
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";

export type PinkasKind = "idea" | "task" | "note";
export type PinkasStatus = "open" | "doing" | "done";
export type PinkasPriority = "low" | "normal" | "high";
export type PinkasAssignee = "gal" | "claude" | "both";

export interface PinkasItem {
  id: string;
  ref: number; // short human reference (#ref) for unambiguous Gal<->Claude talk
  tenant_id: string;
  kind: PinkasKind;
  text: string;
  status: PinkasStatus;
  priority: PinkasPriority | null;
  tags: string[];
  related_task_id: string | null;
  body: Record<string, unknown> | null; // TipTap JSON (@gam/editor) — the "deep" note
  assignee: PinkasAssignee; // the channel core: for gal / claude / both
  pinned: boolean;
  archived: boolean;
  due_date: string | null;
  color: string | null;
  created_by: "gal" | "claude";
  created_at: string;
  updated_at: string;
}

export type PinkasPatch = Partial<
  Pick<PinkasItem, "text" | "status" | "priority" | "kind" | "body" | "assignee" | "pinned" | "archived" | "due_date" | "color" | "tags">
>;

export const PINKAS_QUERY_KEY = ["cc_pinkas"];
const KEY = PINKAS_QUERY_KEY;

export function usePinkas() {
  return useQuery<PinkasItem[]>({
    queryKey: KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cc_pinkas")
        .select("*")
        .eq("tenant_id", "gam")
        .order("pinned", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PinkasItem[];
    },
    refetchInterval: 30_000, // see the other side's entries
  });
}

export function useAddPinkas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      kind?: PinkasKind;
      text: string;
      priority?: PinkasPriority | null;
      assignee?: PinkasAssignee;
      color?: string | null;
      due_date?: string | null;
      tags?: string[];
    }) => {
      const { data, error } = await supabase
        .from("cc_pinkas")
        .insert({
          kind: p.kind ?? "note",
          text: p.text,
          priority: p.priority ?? null,
          assignee: p.assignee ?? "both",
          color: p.color ?? null,
          due_date: p.due_date ?? null,
          tags: p.tags ?? [],
          created_by: "gal",
        })
        .select("*")
        .single();
      if (error) throw error;
      // The workspace opens the new note right away, so it needs the row back.
      return data as PinkasItem;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdatePinkas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: PinkasPatch;
    }) => {
      const { data, error } = await supabase
        .from("cc_pinkas")
        .update(patch)
        .eq("id", id)
        .select("id, updated_at")
        .maybeSingle();
      if (error) throw error;
      // RLS or a deleted row answers with zero rows and no error — that is not a save.
      if (!data) throw new Error("הפתק לא נמצא או שאין הרשאה לעדכן אותו");
      return data as { id: string; updated_at: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeletePinkas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("cc_pinkas").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
